import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  InputLabel,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { UserContext } from "../../auth/auth-context";
import { usePermissions } from "../../../hooks/usePermissions";
import { getEffectivePlan } from "../../../util/utils";
import { useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { FIND_BUSINESS_BY_LICENSE, GET_BUSINESS_CATEGORIES, GET_TOKEN } from "../../../graphql/queries";
import { BUSINESSES_PAGED } from "../../../graphql/queries/business";
import { SEARCH } from "../../../graphql/queries/generic";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedBusiness } from "../../../redux/actions/businessActions";
import { CLAIM_AND_MAP } from "../../../graphql/mutations/care";
import { UPDATE_DEFAULT_BUSINESS } from "../../../graphql/mutations";
import { getToken, setToken } from "../../../util/authUtils";
import jwtDecode from "jwt-decode";
import { idOf, sortByMatchThenClaimed } from "../../../pages/customer/homeShared";
import { detectVerticalFromPath, isVerticalId } from "../../../verticals/registry";
import { getStoredVertical } from "../../../routes/verticalPref";
import { getBrandVertical } from "../../../util/brandDomains";
import {
  filterBusinessCategoriesForProduct,
  getDisplayedBusinessSubcategories,
  getDisplayedBusinessTopLevelCategories,
  getHealthcareTopLevelBusinessCategory,
  isAdultFamilyHomeCategoryName,
  isHealthcareCategoryName,
} from "../../../../core/business/category";
import { ADDITIONAL_BUSINESS_REQUIRED_PLAN, canStartNewBusinessProfile, hasAdditionalBusinessPlanAccess } from "../../../../core/business/businessRegistrationAccess";

const PENDING_KEY = "pendingClaim";
const DASHBOARD_URL = "/biz/dashboard";
const AGREEMENT_CONTEXT_KEY = "pendingAgreementBusinessContext";
const COUNTRY_ID = 228;

export const RegisterBusinessDialog = ({
  open,
  onClose,
  source = "user-profile",
  verticalId = "care",
  title = "Add New Business",
  expectedBusinessId = null,
}: {
  open: boolean;
  onClose: () => void;
  source?: string;
  verticalId?: string;
  title?: string;
  expectedBusinessId?: number | null;
}) => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const { providerUser, updateProviderUser } = useContext(UserContext);
  const [license, setLicense] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessCategoryId, setBusinessCategoryId] = useState("");
  const [businessSubcategoryId, setBusinessSubcategoryId] = useState("");
  const [businessAuthorityConfirmed, setBusinessAuthorityConfirmed] = useState(false);
  const [businessNameSuggestions, setBusinessNameSuggestions] = useState<any[]>([]);
  const [showBusinessNameSuggestions, setShowBusinessNameSuggestions] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupInfo, setLookupInfo] = useState("");
  const [matches, setMatches] = useState<any[]>([]);
  const [loadingLicense, setLoadingLicense] = useState(false);
  const [loadingName, setLoadingName] = useState(false);
  const [loadingNameSuggestions, setLoadingNameSuggestions] = useState(false);
  const [claimingBusinessId, setClaimingBusinessId] = useState<number | null>(null);
  const [lookupMode, setLookupMode] = useState<"license" | "name" | null>(null);
  const [findBusinessByLicense] = useLazyQuery(FIND_BUSINESS_BY_LICENSE, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    returnPartialData: false,
    notifyOnNetworkStatusChange: true,
  });
  const [searchBusinesses] = useLazyQuery(SEARCH, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    returnPartialData: false,
    notifyOnNetworkStatusChange: true,
  });
  const [searchBusinessesPaged] = useLazyQuery(BUSINESSES_PAGED, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    returnPartialData: false,
    notifyOnNetworkStatusChange: true,
  });
  const [claimAndMap] = useMutation(CLAIM_AND_MAP, { awaitRefetchQueries: true });
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const { data: categoryDataRaw } = useQuery(GET_BUSINESS_CATEGORIES);

  const normalizeLicense = (value: string) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  const normalizeText = (value: string) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  const currentCountryId = useMemo(() => {
    const storedCountryId = Number(localStorage.getItem("currentCountryId") || COUNTRY_ID);
    return Number.isFinite(storedCountryId) && storedCountryId > 0
      ? storedCountryId
      : COUNTRY_ID;
  }, []);
  const normalizedLicense = useMemo(() => normalizeLicense(license), [license]);
  const normalizedBusinessName = useMemo(() => normalizeText(businessName), [businessName]);
  const businessCategories = useMemo(
    () => categoryDataRaw?.getBusinessCategories || [],
    [categoryDataRaw]
  );
  const categoryProductId = verticalId === "care" ? "care" : null;
  const scopedBusinessCategories = useMemo(
    () => filterBusinessCategoriesForProduct(businessCategories, categoryProductId),
    [businessCategories, categoryProductId]
  );
  const healthcareTopLevelCategory = useMemo(
    () => getHealthcareTopLevelBusinessCategory(scopedBusinessCategories),
    [scopedBusinessCategories]
  );
  const displayedTopLevelCategories = useMemo(() => {
    return getDisplayedBusinessTopLevelCategories(businessCategories, categoryProductId);
  }, [businessCategories, categoryProductId]);
  const selectedTopLevelCategory = useMemo(
    () =>
      displayedTopLevelCategories.find(
        (category: any) => Number(category?.id) === Number(businessCategoryId || 0)
      ) || null,
    [displayedTopLevelCategories, businessCategoryId]
  );
  const healthcareSubcategoryOptions = useMemo(
    () =>
      getDisplayedBusinessSubcategories(
        businessCategories,
        businessCategoryId || healthcareTopLevelCategory?.id,
        categoryProductId
      ),
    [businessCategories, businessCategoryId, healthcareTopLevelCategory, categoryProductId]
  );
  const selectedHealthcareSubcategory = useMemo(
    () =>
      healthcareSubcategoryOptions.find(
        (category: any) => Number(category?.id) === Number(businessSubcategoryId || 0)
      ) || null,
    [healthcareSubcategoryOptions, businessSubcategoryId]
  );
  const isHealthcareTopLevelSelected =
    Number(selectedTopLevelCategory?.id || 0) === Number(healthcareTopLevelCategory?.id || 0) ||
    isHealthcareCategoryName(selectedTopLevelCategory?.name);
  const requiresHealthcareSubcategory =
    Boolean(healthcareTopLevelCategory) &&
    isHealthcareTopLevelSelected &&
    healthcareSubcategoryOptions.length > 0;
  const isHealthcareFlow = useMemo(() => {
    if (verticalId === "care") {
      return Boolean(selectedTopLevelCategory || selectedHealthcareSubcategory);
    }
    return isHealthcareTopLevelSelected;
  }, [verticalId, selectedTopLevelCategory, selectedHealthcareSubcategory, isHealthcareTopLevelSelected]);
  const isAdultFamilyHomeFlow = useMemo(() => {
    const targetCategoryName =
      selectedHealthcareSubcategory?.name || selectedTopLevelCategory?.name || "";
    return isAdultFamilyHomeCategoryName(targetCategoryName);
  }, [selectedHealthcareSubcategory, selectedTopLevelCategory]);
  const requiresHealthcareLookup = isHealthcareFlow;
  const requiresBusinessNameLookup = isHealthcareFlow && !isAdultFamilyHomeFlow;
  const hasCompletedRequiredLookup =
    !requiresHealthcareLookup ||
    lookupMode === "license" ||
    lookupMode === "name";

  const isClaimedBusiness = (business: any) => {
    const raw = String(business?.claimStatus || "").trim().toUpperCase();
    const hasClaimer =
      business?.createdBy !== null && business?.createdBy !== undefined;
    return hasClaimer || ["CLAIMED", "VERIFIED", "APPROVED", "OWNED"].includes(raw);
  };
  const shouldHideMatchesForSecurity = source === "public-unclaimed-business";
  const resetDialogState = useCallback(() => {
    setLookupError("");
    setLookupInfo("");
    setMatches([]);
    setLicense("");
    setBusinessName("");
    setBusinessCategoryId("");
    setBusinessSubcategoryId("");
    setBusinessAuthorityConfirmed(false);
    setLoadingLicense(false);
    setLoadingName(false);
    setLookupMode(null);
  }, []);

  useEffect(() => {
    if (!open || verticalId !== "care") return;
    if (businessCategoryId) return;
    if (!healthcareTopLevelCategory?.id) return;
    setBusinessCategoryId(String(healthcareTopLevelCategory.id));
  }, [open, verticalId, businessCategoryId, healthcareTopLevelCategory]);

  useEffect(() => {
    if (!open) {
      resetDialogState();
    }
  }, [open, resetDialogState]);

  useEffect(() => {
    let ignore = false;

    if (!open || normalizedBusinessName.length < 2) {
      setBusinessNameSuggestions([]);
      setLoadingNameSuggestions(false);
      return () => {
        ignore = true;
      };
    }

    const timer = window.setTimeout(async () => {
      setLoadingNameSuggestions(true);
      try {
        const result = await searchBusinessesPaged({
          variables: {
            countryId: currentCountryId,
            search: normalizedBusinessName,
            limit: 6,
            offset: 0,
          },
        });
        if (ignore) return;
        const rows = Array.isArray(result?.data?.businessesPaged?.items)
          ? result.data.businessesPaged.items
          : [];
        const rankedRows = sortByMatchThenClaimed(
          rows.filter((item: any, index: number, arr: any[]) => {
            const id = idOf(item);
            return id && arr.findIndex((candidate: any) => idOf(candidate) === id) === index;
          }),
          new Set<string>(),
          normalizedBusinessName
        ).slice(0, 6);
        setBusinessNameSuggestions(rankedRows);
      } catch (_err) {
        if (!ignore) setBusinessNameSuggestions([]);
      } finally {
        if (!ignore) setLoadingNameSuggestions(false);
      }
    }, 250);

    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [currentCountryId, normalizedBusinessName, open, searchBusinessesPaged]);

  const routeToNewBusinessFlow = (licenseNum: string, suggestedBusinessName = "") => {
    const categoryIdForNewBusiness =
      businessCategoryId ||
      (isHealthcareFlow && healthcareTopLevelCategory?.id
        ? String(healthcareTopLevelCategory.id)
        : "");
    const params = new URLSearchParams();
    params.set("v", verticalId);
    params.set("new", "1");
    if (categoryIdForNewBusiness) params.set("categoryId", categoryIdForNewBusiness);
    if (businessSubcategoryId) params.set("subcategoryId", String(businessSubcategoryId));
    if (isHealthcareFlow) {
      params.set("mvr", "1");
      if (isAdultFamilyHomeFlow && licenseNum) params.set("afhLicense", licenseNum);
    }

    navigate(`/biz/business?${params.toString()}`, {
      state: {
        forceNewBusiness: true,
        manualVerificationRequired: isHealthcareFlow,
        source,
        suggestedAfhLicense: isAdultFamilyHomeFlow ? licenseNum || "" : "",
        suggestedBusinessName: suggestedBusinessName || "",
        prefillCategoryId: categoryIdForNewBusiness
          ? Number(categoryIdForNewBusiness)
          : null,
        prefillSubcategoryId: businessSubcategoryId ? Number(businessSubcategoryId) : null,
      },
    });
  };

  const applyLookupResults = useCallback(
    (foundMatches: any[], mode: "license" | "name", emptyMessage: string) => {
      const exactMatches = Array.isArray(foundMatches) ? foundMatches : [];
      setLookupMode(mode);

      if (exactMatches.length === 0) {
        setMatches([]);
        setLookupInfo(emptyMessage);
        setLookupError("");
        return;
      }

      const hasClaimedMatch = exactMatches.some((business: any) =>
        isClaimedBusiness(business)
      );

      if (shouldHideMatchesForSecurity) {
        const matchedCurrentBusiness = exactMatches.find(
          (business: any) =>
            Number(business?.id || 0) > 0 &&
            Number(business?.id || 0) === Number(expectedBusinessId || 0)
        );

        if (matchedCurrentBusiness) {
          setMatches([matchedCurrentBusiness]);
          setLookupError(
            isClaimedBusiness(matchedCurrentBusiness)
              ? "This business is already claimed. If you believe it should belong to you, email support@plany365.com for ownership review."
              : ""
          );
          return;
        }

        setMatches([]);
        setLookupError(
          hasClaimedMatch
            ? "We found a matching business. For security, we cannot show matching records here. If you believe it should belong to you, email support@plany365.com for ownership review."
            : "We found a matching business. For security, we cannot show matching records here. If you are the owner or an authorized administrator, please contact support@plany365.com to continue ownership review."
        );
        return;
      }

      setMatches(exactMatches);
      setLookupInfo(
        hasClaimedMatch
          ? "Matching businesses were found in Plany365. Claimed records are marked below, and you can contact support from that row if needed."
          : "We found a matching business in Plany365. Please use the existing record instead of registering a duplicate."
      );
      setLookupError("");
    },
    [expectedBusinessId, shouldHideMatchesForSecurity]
  );

  const handleUseExistingBusiness = async (business: any) => {
    const businessId = Number(business?.id || 0);
    if (!businessId) return;
    const licenseNumber = normalizeLicense(
      business?.careSettings?.afhLicenseNumber || license
    );
    const pendingPayload = {
      businessId,
      business: {
        id: businessId,
        name: business?.name || "",
        street: business?.street || "",
        cityName: business?.cityName || "",
        stateCode: business?.stateCode || "",
        zipcode: business?.zipcode || "",
        url: business?.url || "",
        images: business?.images ?? [],
      },
      license: licenseNumber || null,
    };

    if (isClaimedBusiness(business)) {
      setLookupError(
        "This business is already claimed. If it should belong to you, email support@plany365.com for ownership review."
      );
      return;
    }

    try {
      setClaimingBusinessId(businessId);
      try {
        sessionStorage.setItem(
          AGREEMENT_CONTEXT_KEY,
          JSON.stringify({
            defaultBusinessId: Number(providerUser?.defaultBusinessId || 0),
            selectedBusinessId: Number(localStorage.getItem("selectedBusinessId") || 0),
          })
        );
      } catch (_err) {
        // ignore storage failures
      }

      if (!getToken()) {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingPayload));
        onClose();
        window.location.assign(`/login?returnTo=${encodeURIComponent("/care/claim-resume")}`);
        return;
      }

      const { data } = await claimAndMap({
        variables: {
          input: {
            businessId,
            businessName: business?.name || undefined,
            licenseNumber: licenseNumber || undefined,
            treatAlreadyClaimedAsSuccess: true,
          },
        },
      });

      const payload = data?.claimAndMapBusiness;
      if (!payload?.success) {
        throw new Error(payload?.message || "Unable to claim this business right now.");
      }

      if (payload?.token) {
        setToken(payload.token);
      }

      try {
        localStorage.setItem("selectedBusinessId", String(payload?.businessId || businessId));
      } catch (_err) {
        // ignore storage failures
      }

      const resolvedBusinessId = Number(payload?.businessId || businessId);
      dispatch(setSelectedBusiness(resolvedBusinessId));
      await updateDefaultBusiness({
        variables: { defaultBusinessId: resolvedBusinessId },
      });
      if (Number(providerUser?.userId || 0) > 0) {
        const refreshedTokenResult = await apolloClient.query({
          query: GET_TOKEN,
          variables: {
            userId: Number(providerUser.userId),
            businessId: resolvedBusinessId,
            staffId: null,
            subscriptionId: null,
          },
          fetchPolicy: "network-only",
        });
        const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
        if (refreshedToken) {
          setToken(refreshedToken);
          updateProviderUser(jwtDecode(refreshedToken));
        } else if (typeof updateProviderUser === "function") {
          updateProviderUser({ defaultBusinessId: resolvedBusinessId });
        }
      } else if (typeof updateProviderUser === "function") {
        updateProviderUser({ defaultBusinessId: resolvedBusinessId });
      }
      onClose();
      window.location.assign(
        `/care/agreement?businessId=${encodeURIComponent(
          String(resolvedBusinessId)
        )}&returnTo=${encodeURIComponent(DASHBOARD_URL)}`
      );
    } catch (err: any) {
      setLookupError(err?.message || "Unable to claim this business right now.");
    } finally {
      setClaimingBusinessId(null);
    }
  };

  const handleLicenseContinue = async () => {
    setLookupError("");
    setLookupInfo("");
    setMatches([]);

    if (!normalizedLicense) {
      setLookupError("License number is required.");
      return;
    }

    setLoadingLicense(true);
    try {
      const { data } = await findBusinessByLicense({
        variables: { licenseNumber: normalizedLicense },
      });
      const exactMatches = (data?.findBusinessByLicense ?? []).filter(
        (business: any) =>
          normalizeLicense(business?.careSettings?.afhLicenseNumber) ===
          normalizedLicense
      );
      applyLookupResults(
        exactMatches,
        "license",
        "No matching AFH license was found. You can continue to create a business profile, and it will be reviewed by a Plany admin before it is treated as verified."
        
      );
    } catch (err: any) {
      setLookupError(
        err?.message || "Could not check AFH license right now. Please try again."
      );
    } finally {
      setLoadingLicense(false);
    }
  };

  const handleBusinessNameSearch = useCallback(() => {
    const run = async () => {
      if (!normalizedBusinessName) {
        setLookupError("Business name is required.");
        return;
      }

      setLookupError("");
      setLookupInfo("");
      setMatches([]);
      setLoadingName(true);

      try {
        const pagedResult = await searchBusinessesPaged({
          variables: {
            countryId: currentCountryId,
            search: normalizedBusinessName,
            limit: 12,
            offset: 0,
          },
        });

        let baseRows = Array.isArray(pagedResult?.data?.businessesPaged?.items)
          ? pagedResult.data.businessesPaged.items
          : [];

        if (baseRows.length === 0) {
          const queryPayload = JSON.stringify({
            searchQuery: normalizedBusinessName,
            locationQuery: "",
            dateQuery: null,
            countryId: currentCountryId,
          });
          const fallbackResult = await searchBusinesses({
            variables: { query: queryPayload },
          });
          baseRows = Array.isArray(fallbackResult?.data?.search)
            ? fallbackResult.data.search
            : [];
        }

        const locallyClaimed = new Set<string>();
        const dedupedRows = (Array.isArray(baseRows) ? baseRows : []).filter((item, index, arr) => {
          const id = idOf(item);
          return id && arr.findIndex((candidate) => idOf(candidate) === id) === index;
        });
        const rankedMatches = sortByMatchThenClaimed(
          dedupedRows,
          locallyClaimed,
          normalizedBusinessName
        ).slice(0, 8);

        applyLookupResults(
          rankedMatches,
          "name",
          "No matching business was found by name. You can still create a business profile, and it will be reviewed by a Plany admin before it is treated as verified."
        );
      } catch (err: any) {
        setMatches([]);
        setLookupInfo("");
        setLookupError(
          err?.message || "Could not search by business name right now. Please try again."
        );
      } finally {
        setLoadingName(false);
      }
    };

    run();
  }, [
    applyLookupResults,
    currentCountryId,
    normalizedBusinessName,
    searchBusinesses,
    searchBusinessesPaged,
  ]);

  const handlePickBusinessNameSuggestion = useCallback((business: any) => {
    const pickedName = String(business?.name || "").trim();
    if (!pickedName) return;
    setBusinessName(pickedName);
    setShowBusinessNameSuggestions(false);
  }, []);

  // The lookup dialog only establishes that a duplicate was not found. Missing
  // subtype details can be completed in the full business form before save.
  const effectiveNewBusinessCategoryId =
    businessCategoryId ||
    (isHealthcareFlow && healthcareTopLevelCategory?.id
      ? String(healthcareTopLevelCategory.id)
      : "");
  const canStartNewProfile = canStartNewBusinessProfile({
    businessCategoryId: effectiveNewBusinessCategoryId,
    requiresHealthcareLookup,
    lookupMode,
    requiresBusinessNameLookup,
    businessAuthorityConfirmed,
  });
  const createBusinessButtonLabel = isHealthcareFlow && !hasCompletedRequiredLookup
    ? isAdultFamilyHomeFlow
      ? "Search by License or Name First"
      : "Search by Name First"
    : isHealthcareFlow
      ? "Create Business For Review"
      : "Create Business Profile";
  const healthcareSubcategoryLabel = verticalId === "care" ? "Care Type" : "Healthcare Type";
  const dialogActionButton =
    matches.length !== 0 ? null : (
      <Button
        variant="contained"
        onClick={() => {
          onClose();
          routeToNewBusinessFlow(normalizedLicense, businessName.trim());
        }}
        disabled={
          !canStartNewProfile
        }
      >
        {createBusinessButtonLabel}
      </Button>
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.2 }}>
          {verticalId === "care"
            ? "Choose the care business type first. Care registration from this dashboard only shows care-related options."
            : "Choose the business type first. We only require lookup and review for healthcare businesses."}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="business-type-select-label">Business Type</InputLabel>
          <Select
            labelId="business-type-select-label"
            label="Business Type"
            value={businessCategoryId}
            onChange={(event) => {
              setBusinessCategoryId(String(event.target.value || ""));
              setBusinessSubcategoryId("");
              setBusinessAuthorityConfirmed(false);
              setMatches([]);
              setLookupInfo("");
              setLookupError("");
              setLookupMode(null);
            }}
            disabled={verticalId === "care" && Boolean(healthcareTopLevelCategory)}
          >
            <MenuItem value="">
              <em>Select one</em>
            </MenuItem>
            {displayedTopLevelCategories.map((category: any) => (
              <MenuItem key={String(category.id)} value={String(category.id)}>
                {String(category.name || "")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {requiresHealthcareSubcategory ? (
          <FormControl fullWidth size="small" sx={{ mt: 1.2 }}>
            <InputLabel id="care-subcategory-select-label">{healthcareSubcategoryLabel}</InputLabel>
            <Select
              labelId="care-subcategory-select-label"
              label={healthcareSubcategoryLabel}
              value={businessSubcategoryId}
              onChange={(event) => {
                setBusinessSubcategoryId(String(event.target.value || ""));
                setBusinessAuthorityConfirmed(false);
                setMatches([]);
                setLookupInfo("");
                setLookupError("");
                setLookupMode(null);
              }}
            >
              <MenuItem value="">
                <em>Select one</em>
              </MenuItem>
              {healthcareSubcategoryOptions.map((category: any) => (
                <MenuItem key={String(category.id)} value={String(category.id)}>
                  {String(category.name || "")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        {isHealthcareFlow ? (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.2 }}>
            {isAdultFamilyHomeFlow
              ? "Healthcare businesses should be checked against existing records first. Search by AFH license or business name before creating a new profile. If no match is found, you can still create the business for admin review."
              : "Healthcare businesses should be checked against existing records first. Search by business name before creating a new profile. If no match is found, you can still create the business for admin review."}
          </Typography>
        ) : businessCategoryId ? (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.2 }}>
            This business type does not require the healthcare record lookup. Continue to create the profile when you are ready.
          </Typography>
        ) : null}
        {isHealthcareFlow && matches.length > 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.2 }}>
            {lookupMode === "name"
              ? "Matching businesses are listed below. Search again by name, or use the existing record instead of creating a duplicate."
              : "A matching business is listed below. Update the license number to search again, or use the existing record instead of creating a duplicate."}
          </Typography>
        ) : null}
        <Stack spacing={1}>
          {isAdultFamilyHomeFlow ? (
            <TextField
              size="small"
              label="License Number"
              placeholder="Example: AFH12345"
              value={license}
              onChange={(e) => {
                setLicense(e.target.value);
                setMatches([]);
                setLookupInfo("");
                if (lookupError) setLookupError("");
                setLookupMode(null);
              }}
              helperText="Use this when you know the AFH license number."
            />
          ) : null}
          {isAdultFamilyHomeFlow ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleLicenseContinue}
                disabled={loadingLicense || !normalizedLicense}
              >
                {loadingLicense ? "Checking..." : "Search by license"}
              </Button>
            </Box>
          ) : null}
          <Box sx={{ position: "relative" }}>
            <TextField
              size="small"
              label="Business Name"
              placeholder="Search by business name"
              value={businessName}
              fullWidth
              onChange={(e) => {
                setBusinessName(e.target.value);
                setShowBusinessNameSuggestions(true);
                setBusinessAuthorityConfirmed(false);
                if (lookupMode === "name") {
                  setMatches([]);
                  setLookupInfo("");
                  setLookupError("");
                }
                setLookupMode(null);
              }}
              onFocus={() => setShowBusinessNameSuggestions(true)}
              onBlur={() => {
                window.setTimeout(() => setShowBusinessNameSuggestions(false), 120);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setShowBusinessNameSuggestions(false);
                  handleBusinessNameSearch();
                }
              }}
              helperText={
                isAdultFamilyHomeFlow
                  ? "Use this if you do not have the license number available."
                  : requiresBusinessNameLookup
                    ? "Search by business name first to check whether this healthcare business already exists."
                  : "We use the name to help prevent duplicate business profiles."
              }
            />
            {showBusinessNameSuggestions &&
            normalizedBusinessName.length >= 2 &&
            (loadingNameSuggestions || businessNameSuggestions.length > 0) ? (
              <Paper
                variant="outlined"
                sx={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  borderRadius: 2,
                  overflow: "hidden",
                  borderColor: "rgba(214,226,240,0.95)",
                  boxShadow: "0 14px 30px rgba(15,23,42,0.16)",
                }}
              >
                {loadingNameSuggestions ? (
                  <Box sx={{ px: 1.25, py: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Searching...
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {businessNameSuggestions.map((business) => {
                      const address = [
                        business?.cityName,
                        business?.stateCode,
                        business?.zipcode,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      return (
                        <ListItemButton
                          key={business?.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePickBusinessNameSuggestion(business);
                          }}
                          sx={{ py: 0.9, px: 1.2, borderBottom: "1px solid #eef3f9" }}
                        >
                          <ListItemText
                            primary={business?.name || "Business"}
                            primaryTypographyProps={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#24384f",
                            }}
                            secondary={address || "Business"}
                            secondaryTypographyProps={{ fontSize: 11, color: "#6d7f95" }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Paper>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBusinessNameSearch}
              disabled={loadingName || !normalizedBusinessName || !isHealthcareFlow}
            >
              {loadingName ? "Searching..." : "Search by name"}
            </Button>
          </Box>
          {requiresBusinessNameLookup ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={businessAuthorityConfirmed}
                  onChange={(event) => setBusinessAuthorityConfirmed(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  I confirm this is a real business and I am authorized to create or manage its profile.
                </Typography>
              }
            />
          ) : null}
          {lookupInfo ? (
            <Alert severity="info">{lookupInfo}</Alert>
          ) : null}
          {lookupError ? (
            <Alert
              severity={
                matches.length > 0 &&
                matches.every((business) => !isClaimedBusiness(business))
                  ? "info"
                  : "error"
              }
            >
              {lookupError}
            </Alert>
          ) : null}
        </Stack>

        {isHealthcareFlow && matches.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {lookupMode === "name"
                ? "Existing business found by name:"
                : isHealthcareFlow
                  ? "Existing business found with this license:"
                  : "Existing business found:"}
            </Typography>
            <Stack spacing={1}>
              {matches.map((business) => {
                const address = [
                  business?.street,
                  business?.cityName,
                  business?.stateCode,
                  business?.zipcode,
                ]
                  .filter(Boolean)
                  .join(", ");
                return (
                  <Box
                    key={business.id}
                    sx={{
                      p: 1.2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {business?.name || "Business"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {address || "No address"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: isClaimedBusiness(business) ? "#b45309" : "#166534",
                        mt: 0.4,
                      }}
                    >
                      {isClaimedBusiness(business) ? "Status: Claimed" : "Status: Unclaimed"}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {isClaimedBusiness(business) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={`mailto:support@plany365.com?subject=${encodeURIComponent(
                            "Ownership review request"
                          )}&body=${encodeURIComponent(
                            `Please review ownership for business:\nName: ${business?.name || ""}\nAFH License: ${
                              business?.careSettings?.afhLicenseNumber || license || ""
                            }\nBusiness Id: ${business?.id || ""}`
                          )}`}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          Email support
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleUseExistingBusiness(business)}
                          disabled={claimingBusinessId === Number(business?.id || 0)}
                          sx={{ textTransform: "none", fontWeight: 700 }}
                        >
                          {claimingBusinessId === Number(business?.id || 0)
                            ? "Claiming..."
                            : "This is my business"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {dialogActionButton}
      </DialogActions>
    </Dialog>
  );
};

const BusinessSettings = ({
  buttonLabel,
  buttonSize = "medium",
  preferredVerticalId = null,
  source = "user-profile",
}: {
  buttonLabel?: string;
  buttonSize?: "small" | "medium" | "large";
  preferredVerticalId?: string | null;
  source?: string;
} = {}) => {
  const { providerUser } = useContext(UserContext);
  const location = useLocation();
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);

  const standardPlanRequired = ADDITIONAL_BUSINESS_REQUIRED_PLAN;

  const { canEditBusiness } = usePermissions();
  const activeProductId = useMemo(() => {
    const detected = detectVerticalFromPath(location.pathname);
    if (detected) return detected;

    const brandVertical = getBrandVertical();
    if (brandVertical) return brandVertical;

    if (preferredVerticalId && isVerticalId(preferredVerticalId)) {
      return preferredVerticalId;
    }
    const params = new URLSearchParams(location.search);
    const verticalFromQuery = String(params.get("v") || "").trim();
    if (verticalFromQuery && isVerticalId(verticalFromQuery)) return verticalFromQuery;
    const stored = getStoredVertical();
    if (stored && isVerticalId(stored)) return stored;
    return "care";
  }, [preferredVerticalId, location.pathname, location.search]);
  const effectiveButtonLabel = buttonLabel || "Add New Business";
  const dialogTitle = "Add New Business";

  const canCreateBusiness = providerUser?.isPlatformAdmin === true || canEditBusiness();

  const handleCreateBusiness = () => {
    if (!canCreateBusiness) return;
    const effectivePlan = getEffectivePlan(providerUser);

    if (!hasAdditionalBusinessPlanAccess(providerUser, effectivePlan)) {
      setCheckPlanNeeded(true);
    } else {
      setLicenseDialogOpen(true);
    }
  };

  return (
    <div>
      <Button
        variant="contained"
        onClick={handleCreateBusiness}
        disabled={!canCreateBusiness}
        size={buttonSize}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          bgcolor: "#22374a",
          "&:hover": { bgcolor: "#1b2f41" },
        }}
      >
        {effectiveButtonLabel}
      </Button>

      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Create an additional business profile"
          requiredPlanId={standardPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}

      <RegisterBusinessDialog
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        source={source}
        verticalId={activeProductId}
        title={dialogTitle}
      />
    </div>
  );
};

export default BusinessSettings;
export {};
